import styled from "styled-components";
import { screenSize, theme } from "@src/theme";
import {
  TopBarColumn,
  TopBarSection,
} from "@src/components/PageTopSection/PageTopSection";
import {
  FIGMA_CHAT_COLUMN_MAX_WIDTH,
  FIGMA_CHAT_MESSAGE_MAX_WIDTH,
  FIGMA_MESSAGE_TEXT,
  FIGMA_SELECTED_ROW,
  FIGMA_SIDEBAR_BG,
  FIGMA_SIDEBAR_WIDTH,
  FIGMA_USER_NAME_PILL,
} from "../Messages/MessagesPage.styles";

/**
 * The header's two columns sit side by side on a desktop, but below a tablet
 * the search box and pager have nowhere to go and end up on top of the status
 * chips, so they stack instead.
 */
export const HeaderGrid = styled(TopBarSection)`
  overflow: visible;

  @media (max-width: ${screenSize.tablet}) {
    grid-template-columns: 1fr;
    gap: ${theme.margins.medium};
  }
`;

export const HeaderControlsColumn = styled(TopBarColumn)`
  align-items: flex-end;
  box-sizing: border-box;
  gap: 12px;
  overflow: visible;
  padding-right: 4px;

  @media (max-width: ${screenSize.tablet}) {
    align-items: flex-start;
    padding-right: 0;
  }
`;

export const Subtitle = styled.p`
  color: ${theme.colors.gray150};
  font-size: 13px;
  line-height: 18px;
  margin: 4px 0 0;
`;

export const FiltersRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;
`;

export const SearchInput = styled.input`
  background-color: ${theme.colors.darkBg};
  border: 1px solid ${theme.colors.darkGray};
  border-radius: 999px;
  color: ${theme.colors.white};
  font-size: 14px;
  height: 32px;
  padding: 0 14px;
  width: 240px;
  outline: none;

  &::placeholder {
    color: ${theme.colors.gray150};
  }
  &:focus {
    border-color: ${theme.colors.yellow};
  }
`;

export const PaginationRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${theme.colors.gray150};
  font-size: 12px;
`;

export const PagerButton = styled.button`
  background-color: transparent;
  border: 1px solid ${theme.colors.yellow};
  border-radius: 999px;
  color: ${theme.colors.yellow};
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  height: 27px;
  padding: 0 14px;

  &:disabled {
    border-color: ${theme.colors.darkGray};
    color: ${theme.colors.darkGray};
    cursor: not-allowed;
  }
`;

export const SidebarContainer = styled.div`
  width: ${FIGMA_SIDEBAR_WIDTH};
  background-color: ${FIGMA_SIDEBAR_BG};
  border-right: 1px solid ${theme.colors.darkGray};
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  min-height: 0;
`;

export const SidebarList = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 9px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(155, 155, 155, 0.43);
    border-radius: 999px;
  }
  scrollbar-width: thin;
  scrollbar-color: rgba(155, 155, 155, 0.43) transparent;
`;

export const ConversationRow = styled.div<{ $selected: boolean }>`
  border-bottom: 1px solid ${theme.colors.darkGray};
  box-shadow: ${(props) =>
    props.$selected ? `inset 3px 0 0 ${theme.colors.yellow}` : "none"};
  background-color: ${(props) =>
    props.$selected ? FIGMA_SELECTED_ROW : "transparent"};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px;

  &:hover {
    background-color: ${FIGMA_SELECTED_ROW};
  }
`;

export const RowTopLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

export const RowParticipants = styled.span`
  color: ${theme.colors.gray};
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: -0.02em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const RowCuratorName = styled.span`
  color: ${theme.colors.yellow};
`;

export const RowTime = styled.span`
  color: ${FIGMA_MESSAGE_TEXT};
  flex-shrink: 0;
  font-size: 10px;
  line-height: 19px;
`;

export const RowBottomLine = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const RowPreview = styled.span`
  color: ${FIGMA_MESSAGE_TEXT};
  flex: 1;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.02em;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const STATUS_COLORS: Record<string, string> = {
  requested: "#7FB3FF",
  active: "#7BD88F",
  ended: "#979794",
  blocked: "#FF8A8A",
  ai_blocked: "#FFB86C",
};

export const StatusPill = styled.span<{ $status: string }>`
  border: 1px solid ${(props) => STATUS_COLORS[props.$status] || "#979794"};
  border-radius: 999px;
  color: ${(props) => STATUS_COLORS[props.$status] || "#979794"};
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 16px;
  padding: 0 8px;
  text-transform: uppercase;
`;

export const TranscriptContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: ${theme.colors.darkBg};
  min-height: 0;
  min-width: 0;
  padding: 32px 40px 28px;
`;

export const TranscriptColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  max-width: ${FIGMA_CHAT_COLUMN_MAX_WIDTH};
  min-height: 0;
  width: 100%;
`;

export const TranscriptHeader = styled.div`
  border-bottom: 1px solid ${theme.colors.darkGray};
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-shrink: 0;
  padding-bottom: 16px;
`;

export const TranscriptTitle = styled.h3`
  color: ${theme.colors.white};
  font-size: 16px;
  font-weight: 700;
  margin: 0;
`;

export const TranscriptMeta = styled.span`
  color: ${theme.colors.gray150};
  font-size: 12px;
  line-height: 18px;
`;

export const MessagesArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 28px;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding-top: 24px;

  &::-webkit-scrollbar {
    width: 9px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(155, 155, 155, 0.43);
    border-radius: 999px;
  }
  scrollbar-width: thin;
  scrollbar-color: rgba(155, 155, 155, 0.43) transparent;
`;

export const MessageRow = styled.div<{ $isCurator: boolean }>`
  align-items: ${(props) => (props.$isCurator ? "flex-end" : "flex-start")};
  align-self: ${(props) => (props.$isCurator ? "flex-end" : "flex-start")};
  display: flex;
  flex-direction: column;
  max-width: ${FIGMA_CHAT_MESSAGE_MAX_WIDTH};
  width: fit-content;
`;

export const CuratorNamePill = styled.div`
  background-color: ${theme.colors.yellow};
  border-radius: 13px;
  color: ${theme.colors.black};
  font-size: 15.4px;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: -0.02em;
  margin-bottom: 10px;
  padding: 1px 11px;
  width: fit-content;
`;

export const ListenerNamePill = styled(CuratorNamePill)`
  background-color: ${FIGMA_USER_NAME_PILL};
`;

export const MessageText = styled.p<{ $isCurator: boolean }>`
  color: ${FIGMA_MESSAGE_TEXT};
  font-size: 15.4px;
  line-height: 22px;
  margin: 0;
  text-align: ${(props) => (props.$isCurator ? "right" : "left")};
  width: 100%;
  word-break: break-word;
`;

export const MessageTime = styled.span<{ $isCurator: boolean }>`
  align-self: ${(props) => (props.$isCurator ? "flex-end" : "flex-start")};
  color: ${FIGMA_MESSAGE_TEXT};
  font-size: 10px;
  letter-spacing: -0.02em;
  line-height: 19px;
  margin-top: 8px;
  opacity: 0.5;
`;

export const CenteredNote = styled.div`
  align-items: center;
  color: ${theme.colors.gray150};
  display: flex;
  flex: 1;
  font-size: 14px;
  justify-content: center;
  padding: 24px;
  text-align: center;
`;

export const LoadOlderRow = styled.div`
  display: flex;
  justify-content: center;
  padding-bottom: 8px;
`;

export const ReadOnlyFooter = styled.div`
  border-top: 1px solid ${theme.colors.darkGray};
  color: ${theme.colors.gray150};
  flex-shrink: 0;
  font-size: 12px;
  margin-top: 16px;
  padding-top: 16px;
  text-align: center;
`;
